import React from "react";
import { Card, Space, Flex, Typography } from "antd";
import { BankOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";

const { Text, Title } = Typography;

interface MainCategoryTotals {
  totalDebit: number;
  totalCredit: number;
  balance: number;
}

interface FinanceTreeSummaryProps {
  mainCategoryTotals: MainCategoryTotals;
}

const FinanceTreeSummary: React.FC<FinanceTreeSummaryProps> = ({ mainCategoryTotals }) => {
  const { t } = useTranslation();

  return (
    <Card
      variant="borderless"
      style={{
        borderRadius: "12px",
        background: "linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)",
        border: "1px solid rgba(102, 126, 234, 0.2)",
        boxShadow: "0 2px 12px rgba(102, 126, 234, 0.1)",
      }}
      styles={{ body: { padding: "20px" } }}
    >
      <Space direction="vertical" size="middle" style={{ width: "100%" }}>
        <Flex justify="flex-start" align="center">
          <Space size="small">
            <BankOutlined style={{ fontSize: "20px", color: "#667eea" }} />
            <Text
              strong
              style={{
                fontSize: "16px",
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
                fontWeight: 600,
              }}
            >
              {t("label.main_categories")}
            </Text>
          </Space>
        </Flex>

        <Flex gap={12} wrap="wrap" style={{ width: "100%" }}>
          <Card
            variant="borderless"
            style={{
              flex: 1,
              minWidth: "200px",
              borderRadius: "10px",
              background: "linear-gradient(135deg, #fff7e6 0%, #ffe7ba 100%)",
              border: "1px solid #ffd591",
              boxShadow: "0 2px 6px rgba(250, 173, 20, 0.12)",
            }}
            styles={{ body: { padding: "16px" } }}
          >
            <Space direction="vertical" size={4} style={{ width: "100%" }}>
              <Text
                style={{
                  color: "#d48806",
                  fontSize: "12px",
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: "0.3px",
                }}
              >
                {t("label.total_debit")}
              </Text>
              <Title
                level={3}
                style={{
                  margin: 0,
                  color: "#ad4e00",
                  fontWeight: 700,
                  fontSize: "24px",
                }}
              >
                {mainCategoryTotals.totalDebit.toLocaleString()}
              </Title>
              <Text type="secondary" style={{ fontSize: "11px" }}>
                {t("label.currency")}
              </Text>
            </Space>
          </Card>

          <Card
            variant="borderless"
            style={{
              flex: 1,
              minWidth: "200px",
              borderRadius: "10px",
              background: "linear-gradient(135deg, #e6fffb 0%, #b5f5ec 100%)",
              border: "1px solid #87e8de",
              boxShadow: "0 2px 6px rgba(19, 194, 194, 0.12)",
            }}
            styles={{ body: { padding: "16px" } }}
          >
            <Space direction="vertical" size={4} style={{ width: "100%" }}>
              <Text
                style={{
                  color: "#08979c",
                  fontSize: "12px",
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: "0.3px",
                }}
              >
                {t("label.total_credit")}
              </Text>
              <Title
                level={3}
                style={{
                  margin: 0,
                  color: "#006d75",
                  fontWeight: 700,
                  fontSize: "24px",
                }}
              >
                {mainCategoryTotals.totalCredit.toLocaleString()}
              </Title>
              <Text type="secondary" style={{ fontSize: "11px" }}>
                {t("label.currency")}
              </Text>
            </Space>
          </Card>

          <Card
            variant="borderless"
            style={{
              flex: 1,
              minWidth: "200px",
              borderRadius: "10px",
              background:
                mainCategoryTotals.balance >= 0
                  ? "linear-gradient(135deg, #f6ffed 0%, #d9f7be 100%)"
                  : "linear-gradient(135deg, #fff1f0 0%, #ffccc7 100%)",
              border:
                mainCategoryTotals.balance >= 0 ? "1px solid #b7eb8f" : "1px solid #ffa39e",
              boxShadow:
                mainCategoryTotals.balance >= 0
                  ? "0 2px 6px rgba(82, 196, 26, 0.12)"
                  : "0 2px 6px rgba(255, 77, 79, 0.12)",
            }}
            styles={{ body: { padding: "16px" } }}
          >
            <Space direction="vertical" size={4} style={{ width: "100%" }}>
              <Text
                style={{
                  color: mainCategoryTotals.balance >= 0 ? "#389e0d" : "#cf1322",
                  fontSize: "12px",
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: "0.3px",
                }}
              >
                {t("label.balance")}
              </Text>
              <Title
                level={3}
                style={{
                  margin: 0,
                  color: mainCategoryTotals.balance >= 0 ? "#237804" : "#a8071a",
                  fontWeight: 700,
                  fontSize: "24px",
                }}
              >
                {mainCategoryTotals.balance >= 0 ? "+" : ""}
                {mainCategoryTotals.balance.toLocaleString()}
              </Title>
              <Text type="secondary" style={{ fontSize: "11px" }}>
                {t("label.currency")}
              </Text>
            </Space>
          </Card>
        </Flex>
      </Space>
    </Card>
  );
};

export default FinanceTreeSummary;

